import React, {useMemo, useState} from "react";
import {Team} from "../api";
import {ConfigInterface} from "../config";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleNotch, faTimes} from "@fortawesome/free-solid-svg-icons";
import {ChatMessage} from "./types/chat-message";
import {useChatContext} from "./hooks/use-chat-context";
import {useChatMetadataContext} from "./hooks/use-chat-metadata-context";
import {SidebarItem} from "./types/chat-sidebar";
import {AddChatForm} from "./components/AddChatForm";
import {ChatConversationList} from "./components/ChatConversationList";

export const ChatSidebar: React.FC<{config: ConfigInterface, teams: Team[], primaryTeam: Team|null, secondaryTeams: Team[], employees: {sub: string, team_id: number|null}[]}> = (props) => {
  const {conversations, messages, currentConversationId, loaded: loadedChats} = useChatContext()
  const [mode, setMode] = useState<"list"|"create">("list")

  const messagesSortedByTime = useMemo(() => {
    return messages.sort((a, b) => a.timestamp.getTime() < b.timestamp.getTime() ? 1 : -1)
  }, [messages])

  const employeeTeamMap = useMemo(() => {
    const map: {[sub: string]: number} = {}
    props.employees.forEach((employee) => {
      if (employee.team_id) {
        map[employee.sub] = employee.team_id
      }
    })
    return map
  }, [props.employees])

  const {getMetadata} = useChatMetadataContext()
  const sidebarItems = useMemo(() => {
    const items: SidebarItem[] = []
    for (const conversation of conversations) {
      const lastMessage: ChatMessage|undefined = messagesSortedByTime.filter(m => m.conversationId === conversation.id)[0]
      const metadata = getMetadata(conversation.id)
      const lastOpenedDate = metadata ? (new Date(metadata.last_opened)).getTime() : null
      const unreadMessages = lastOpenedDate ? messagesSortedByTime.filter(m => m.conversationId === conversation.id && m.timestamp.getTime() > lastOpenedDate).length : 0
      items.push({
        conversationId: conversation.id,
        name: conversation.name,
        lastMessage: lastMessage,
        unread: unreadMessages,
        teamId: conversation?.teamId,
        selected: currentConversationId === conversation.id,
        isPurged: conversation.purgedAt !== null,
        members: conversation.members?.map(m => {
          return {
            ...m,
            teamId: employeeTeamMap[m.sub] ?? null,
          }
        }) ?? []
      })
    }
    return items.sort((a,b) => {
      if (a.lastMessage === undefined || b.lastMessage === undefined) {
        return 0
      }
      return a.lastMessage.timestamp.getTime() < b.lastMessage.timestamp.getTime() ? 1 : -1
    })
  }, [conversations, messagesSortedByTime, currentConversationId, getMetadata, employeeTeamMap])

  const loaded = useMemo(() => {
    // TODO: Expand, this is not yet fully functional.
    return loadedChats
  }, [loadedChats])


  if (! loaded) {
    return <div className={"w-64 xl:w-96 border-r border-gray-100"}></div>
  }

  // Allow users to add new chats
  if (mode === "create") {
    return <div className={"w-64 xl:w-96 border-r border-gray-100 overflow-y-scroll"}>
      {/* Header */}
      <div className={"h-16 flex items-center justify-between px-4 border-b border-gray-100"}>
        <div className={"text-lg font-bold"}>Chat toevoegen</div>
        <button className={"hover:bg-gray-50 h-7 w-7 flex items-center justify-center rounded-full"} onClick={() => setMode("list")}>
          <FontAwesomeIcon icon={faTimes} className={'text-xl'} />
        </button>
      </div>
      <div className={"p-4"}>
        <AddChatForm config={props.config} onComplete={() => setMode("list")} existingChats={sidebarItems} />
      </div>
    </div>
  }

  if (! loaded) {
    return <div className={'w-64 xl:w-96 border-r border-gray-100 overflow-y-scroll flex items-center justify-center'}>
      <div className={"flex flex-col space-y-2 items-center justify-center text-slate-800"}>
        <FontAwesomeIcon icon={faCircleNotch} spin={true} className={"text-xl text-brand-primary mb-3"} />
        Laden...
      </div>
    </div>
  }

  // Show all available chats
  return <ChatConversationList items={sidebarItems} addChat={() => setMode('create')} teams={props.teams} primaryTeam={props.primaryTeam} secondaryTeams={props.secondaryTeams} />
}

